import React, { Suspense, lazy, useEffect, useState, useCallback } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { CssVarsProvider } from '@mui/joy/styles';
import { Box, CircularProgress } from '@mui/joy';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from 'react-query';
import messenger from './components/messenger';
import ProtectedRoute from './components/ProtectedRoute.jsx';

// Komponenten, die sofort benötigt werden
import Sidebar from './components/Sidebar';
import TopNav from './components/topnav';
import BreadcrumbsComponent from './components/BreadcrumbsComponent';
import GeneralSettings from './pages/GeneralSettings';

// Lazy-loaded Komponenten
const HomePage = lazy(() => import('./pages/homepage'));
const Page1 = lazy(() => import('./pages/Page1'));
const FleetPartner = lazy(() => import('./pages/FleetPartner'));
const OrganizationDetails = lazy(() => import('./pages/OrganizationDetails'));
const AddPartner = lazy(() => import('./pages/AddPartner'));
const AddApplicant = lazy(() => import('./pages/AddApplicant'));
const PartnerDetails = lazy(() => import('./pages/PartnerDetails'));
const ApplicantDetails = lazy(() => import('./pages/ApplicantDetails'));
const Brands = lazy(() => import('./pages/brands'));
const Locations = lazy(() => import('./pages/locations'));
const Waitlist = lazy(() => import('./pages/waitlist'));
const Applicants = lazy(() => import('./pages/applicants'));
// LoginPage entfällt, wir springen direkt zu Google im ProtectedRoute
const Assets = lazy(() => import('./pages/assets'));
const UserManagement = lazy(() => import('./pages/user-management'));
const OrderList = lazy(() => import('./pages/OrderList'));
const BillingManagement = lazy(() => import('./pages/BillingManagement'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));
const Uploads = lazy(() => import('./pages/Uploads'));
const Reports = lazy(() => import('./pages/Reports'));
const Drivers = lazy(() => import('./pages/drivers'));
const Analytics = lazy(() => import('./pages/analytics'));
const DriversAnalytics = lazy(() => import('./pages/analytics/DriversAnalytics'));
const OrganizationsAnalytics = lazy(() => import('./pages/analytics/OrganizationsAnalytics'));
const LocationsAnalytics = lazy(() => import('./pages/analytics/LocationsAnalytics'));
const DocumentsAnalytics = lazy(() => import('./pages/analytics/DocumentsAnalytics'));
const OnboardingAnalytics = lazy(() => import('./pages/analytics/OnboardingAnalytics'));
const Documents = lazy(() => import('./pages/documents'));
const DocumentReview = lazy(() => import('./pages/DocumentReview'));

// Prefetching-Funktion
const prefetchComponent = (importFunc) => {
  importFunc();
};

// Optimierter LoadingFallback
const LoadingFallback = React.memo(() => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
));

// QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function App() {
  const { isAuthenticated, isLoading, getAccessTokenSilently, error } = useAuth0();
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const location = useLocation();

  const handleSidebarResize = useCallback((width) => {
    setSidebarWidth(width);
  }, []);

  // Kein loginWithRedirect() hier! Token nur prüfen, wenn bereits eingeloggt
  useEffect(() => {
    const validateTokenIfLoggedIn = async () => {
      if (isAuthenticated) {
        try {
          await getAccessTokenSilently();
          setIsTokenValid(true);
        } catch (e) {
          console.error('Token validation failed:', e);
          setIsTokenValid(false);
        }
      } else {
        setIsTokenValid(false);
      }
    };
    validateTokenIfLoggedIn();
  }, [isAuthenticated, getAccessTokenSilently]);

  // Prefetch nur wenn eingeloggt
  useEffect(() => {
    if (isAuthenticated && isTokenValid && location.pathname === '/') {
      prefetchComponent(() => import('./pages/Page1'));
      prefetchComponent(() => import('./pages/OrderList'));
    }
  }, [isAuthenticated, isTokenValid, location]);

  // Loader solange Auth0 lädt
  if (isLoading) return <LoadingFallback />;

  if (error) {
    return <div>Fehler: {error.message}</div>;
  }

  // Layout nur anzeigen, wenn eingeloggt – sonst kein UI-Flash.
  const Layout = ({ children }) => (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {isAuthenticated && <Sidebar onResize={handleSidebarResize} />}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          width: isAuthenticated ? `calc(100% - ${sidebarWidth}px)` : '100%',
          marginLeft: isAuthenticated ? `${sidebarWidth}px` : 0,
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {/* TopNav/Breadcrumbs kannst du hier bei Bedarf wieder einblenden */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );

  return (
    <QueryClientProvider client={queryClient}>
      <CssVarsProvider>
        <messenger>
          <Layout>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Öffentliche Routen (Login entfällt; Unauthorized kannst du behalten) */}
                <Route path="/unauthorized" element={<Unauthorized />} />

                {/* Geschützte Routen */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/page1"
                  element={
                    <ProtectedRoute>
                      <Page1 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orderlist"
                  element={
                    <ProtectedRoute>
                      <OrderList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/FleetPartner"
                  element={
                    <ProtectedRoute>
                      <FleetPartner />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/organization-details/:organizationId"
                  element={
                    <ProtectedRoute>
                      <OrganizationDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/brands"
                  element={
                    <ProtectedRoute>
                      <Brands />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/locations"
                  element={
                    <ProtectedRoute>
                      <Locations />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/add-partner"
                  element={
                    <ProtectedRoute>
                      <AddPartner />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partner-details/:partnerId"
                  element={
                    <ProtectedRoute>
                      <PartnerDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/locations/:city/waitlist"
                  element={
                    <ProtectedRoute>
                      <Waitlist />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/assets"
                  element={
                    <ProtectedRoute>
                      <Assets />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/BillingManagement"
                  element={
                    <ProtectedRoute>
                      <BillingManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/drivers"
                  element={
                    <ProtectedRoute>
                      <Drivers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/applicants/add"
                  element={
                    <ProtectedRoute>
                      <AddApplicant />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/driver/:applicantId"
                  element={
                    <ProtectedRoute>
                      <ApplicantDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/applicants"
                  element={
                    <ProtectedRoute>
                      <Applicants />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user-management"
                  element={
                    <ProtectedRoute>
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <GeneralSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/uploads"
                  element={
                    <ProtectedRoute>
                      <Uploads />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <Analytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics/drivers"
                  element={
                    <ProtectedRoute>
                      <DriversAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics/organizations"
                  element={
                    <ProtectedRoute>
                      <OrganizationsAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics/locations"
                  element={
                    <ProtectedRoute>
                      <LocationsAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics/documents"
                  element={
                    <ProtectedRoute>
                      <DocumentsAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics/onboarding"
                  element={
                    <ProtectedRoute>
                      <OnboardingAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/documents"
                  element={
                    <ProtectedRoute>
                      <Documents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/documents/:documentId"
                  element={
                    <ProtectedRoute>
                      <DocumentReview />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
          </Layout>
          <Toaster 
            position="top-center"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#1e293b',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: '500',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </messenger>
      </CssVarsProvider>
    </QueryClientProvider>
  );
}

export default App;
